import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Phone, Mail, Home, Search, Radio, Clock, BookUser, Users, MapPin, Smartphone, Copy } from 'lucide-react';
import { toLocalYMD } from '../utils/dateUtils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  tier1: 'Tier 1', tier2: 'Tier 2', team_lead: 'Team Lead',
  solution_engineer: 'Solution Engineer', admin: 'Admin', manager: 'Manager',
  viewer: 'Viewer', employee: 'Employee',
};
const prettifyRole = (role) => {
  if (!role) return '';
  return ROLE_LABELS[role] || role.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
};

const initials = (name) => (name || '?').trim().split(/\s+/).map((n) => n[0]).slice(0, 2).join('').toUpperCase();

const copyContact = async (p) => {
  const parts = [p.name];
  if (p.job_title) parts.push(p.job_title);
  if (p.email) parts.push(`Email: ${p.email}`);
  if (p.work_phone) parts.push(`Work: ${p.work_phone}`);
  if (p.phone) parts.push(`Mobile: ${p.phone}`);
  if (p.home_phone) parts.push(`Home: ${p.home_phone}`);
  try {
    await navigator.clipboard.writeText(parts.filter(Boolean).join('\n'));
    toast.success(`Copied ${p.name}'s contact`);
  } catch {
    toast.error('Could not copy to clipboard');
  }
};

const CopyBtn = ({ person }) => (
  <button
    onClick={() => copyContact(person)}
    data-testid={`copy-contact-${person.id}`}
    className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition-colors p-1 rounded"
    title="Copy contact"
  >
    <Copy className="h-4 w-4" />
  </button>
);

const ContactLines = ({ person }) => {
  const lines = [];
  if (person.email) lines.push({ icon: Mail, href: `mailto:${person.email}`, text: person.email, key: 'email' });
  if (person.work_phone) lines.push({ icon: Phone, href: `tel:${person.work_phone}`, text: `${person.work_phone}`, label: 'Work', key: 'work' });
  if (person.phone) lines.push({ icon: Smartphone, href: `tel:${person.phone}`, text: `${person.phone}`, label: 'Mobile', key: 'mobile' });
  if (person.home_phone) lines.push({ icon: Home, href: `tel:${person.home_phone}`, text: `${person.home_phone}`, label: 'Home', key: 'home' });
  if (lines.length === 0) {
    return <p className="text-xs text-gray-400 italic mt-1" data-testid={`no-contact-${person.id}`}>No contact info on file</p>;
  }
  return (
    <div className="mt-2 space-y-1">
      {lines.map((l) => {
        const Icon = l.icon;
        return (
          <a
            key={l.key}
            href={l.href}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
            data-testid={`contact-${l.key}-${person.id}`}
          >
            <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span className="truncate">{l.text}</span>
            {l.label && <span className="text-[10px] uppercase tracking-wide text-gray-400">{l.label}</span>}
          </a>
        );
      })}
    </div>
  );
};

const Directory = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ directory: [], on_now: [], hidden_fields: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchData = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    const now = new Date();
    const date = toLocalYMD(now);
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    try {
      const res = await axios.get(`${API}/directory`, {
        params: { date, time },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || { directory: [], on_now: [] });
    } catch (e) {
      console.error('Failed to load directory', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData(true);
    // Keep the "On Now" view fresh as shifts start/end.
    const interval = setInterval(() => fetchData(false), 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const roles = useMemo(() => {
    const set = new Set((data.directory || []).map((d) => d.role).filter(Boolean));
    return Array.from(set).sort();
  }, [data.directory]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.directory || []).filter((d) => {
      if (roleFilter !== 'all' && d.role !== roleFilter) return false;
      if (!q) return true;
      return [d.name, d.job_title, d.team_name, d.email, prettifyRole(d.role)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data.directory, search, roleFilter]);

  const onNow = data.on_now || [];

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="directory-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-blue-600 rounded-xl">
          <BookUser className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roster &amp; Contacts</h1>
          <p className="text-sm text-gray-500">See who's on call right now and how to reach the team.</p>
        </div>
      </div>

      <Tabs defaultValue="on-now" className="w-full">
        <TabsList data-testid="directory-tabs">
          <TabsTrigger value="on-now" data-testid="tab-on-now">
            <Radio className="h-4 w-4 mr-2" /> On Now
            {onNow.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-green-100 text-green-700">
                {onNow.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="directory" data-testid="tab-directory">
            <Users className="h-4 w-4 mr-2" /> Directory
          </TabsTrigger>
        </TabsList>

        {/* ON NOW */}
        <TabsContent value="on-now" className="mt-4">
          {loading ? (
            <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
          ) : onNow.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center" data-testid="on-now-empty">
                <Clock className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No one is currently on call or on shift</p>
                <p className="text-sm text-gray-400 mt-1">Check the Directory tab to reach anyone on the team.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {onNow.map((p) => {
                const isOnCall = p.shift_type === 'on_call';
                return (
                  <Card
                    key={`${p.id}-${p.shift_type}`}
                    className={`border-l-4 ${isOnCall ? 'border-l-orange-500' : 'border-l-green-500'}`}
                    data-testid={`on-now-card-${p.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className={`w-11 h-11 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 ${isOnCall ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                            {initials(p.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{p.name}</p>
                            <p className="text-xs text-gray-500">{p.job_title || prettifyRole(p.role)}</p>
                            <ContactLines person={p} />
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <Badge className={isOnCall ? 'bg-orange-500 hover:bg-orange-500' : 'bg-green-600 hover:bg-green-600'}>
                            {isOnCall ? 'On Call' : 'On Shift'}
                          </Badge>
                          <p className="text-[11px] text-gray-500 mt-1.5 whitespace-nowrap">
                            {formatTime(p.start_time)} – {formatTime(p.end_time)}
                          </p>
                          {p.work_location && (
                            <p className="text-[10px] text-gray-400 flex items-center justify-end gap-1 mt-0.5">
                              <MapPin className="h-3 w-3" />{p.work_location === 'remote' ? 'Remote' : 'Office'}
                            </p>
                          )}
                          <div className="mt-1.5">
                            <CopyBtn person={p} />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* DIRECTORY */}
        <TabsContent value="directory" className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="directory-search"
                placeholder="Search by name, title, team, email…"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="directory-role-filter">
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r} value={r}>{prettifyRole(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center" data-testid="directory-empty">
                <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No team members found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((p) => (
                <Card key={p.id} data-testid={`directory-card-${p.id}`} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm shrink-0">
                        {initials(p.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-gray-900 truncate" data-testid={`directory-name-${p.id}`}>{p.name}</p>
                          <CopyBtn person={p} />
                        </div>
                        {p.job_title && <p className="text-xs text-gray-500 truncate">{p.job_title}</p>}
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          <Badge variant="secondary" className="text-[10px]">{prettifyRole(p.role)}</Badge>
                          {p.team_name && <Badge variant="outline" className="text-[10px]">{p.team_name}</Badge>}
                        </div>
                        <ContactLines person={p} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Directory;
